import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@dfh-corp-sites/ui'
import { cn } from '@dfh-corp-sites/ui/utils'
import { graphql } from 'gatsby'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import useMediaQuery from '../../../hooks/useMediaQuery'
import useSiteSettings from '../../../hooks/useSiteSettings'
import { publishViewCartEvent } from '../../../lib/analytics/cart'
import useCustomerDisplayName from '../../../lib/auth/hooks/useCustomerDisplayName'
import { ToggleCartContext, useCartContext } from '../../../lib/cart'
import { totalQuantityExcludingDependents } from '../../../lib/cart/item'
import { useShouldHideHeader } from '../../../lib/header/hooks/useShouldHideHeader'
import {
  setActiveItem,
  setShowSearch,
  useActiveItem,
  useShowSearch,
  useUtilityMenuState,
} from '../../../lib/header/state'
import {
  stringifyHeaderStyle,
  useHeaderStyle,
  whiteHeaderStyleClasses,
} from '../../../lib/header/style'
import { useCanonicalLocation } from '../../../lib/pages/hooks/useCanonicalLocation'
import { useSanityRoleAndContextBasedContent } from '../../../lib/sanity'
import { isLoginMenuEntry, isRegistrationMenuEntry } from '../../../utils/menuEntryTools'
import { filterMenuEntriesToDisplay } from '../../../utils/sanity/documents/menuEntry'
import Link from '../Link'
import CartIcon from './CartIcon'
import { DesktopMainMenuEntry } from './DesktopMainMenuEntry'
import MenuEntryLink from './MenuEntryLink'
import { SearchContainer } from './SearchContainer'
import * as UI from './ui'

import SearchIcon from '../../../img/icons/search.svg'

interface DesktopHeaderProps {
  threshold: number
}

const AccountDropdown: React.FC = () => {
  const { utilityMenu: menuEntriesUnfiltered } = useSiteSettings() || {}
  const isNarrow = useMediaQuery('(max-width: 1023px)')
  const customerDisplayName = useCustomerDisplayName()
  const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
  const { isAuthenticated, isVisible } = useUtilityMenuState()

  const { headerStyle, classes: headerStyleClasses } = useHeaderStyle()

  const menuEntries = useMemo(
    () => filterMenuEntriesToDisplay(shouldDisplayContent)(menuEntriesUnfiltered),
    [menuEntriesUnfiltered, shouldDisplayContent],
  )

  const onFocusStyles = cn(
    'focus:outline-none',
    'focus-visible:ring-1',
    'focus-visible:ring-offset-2',
    'focus-visible:ring-offset-blue-600',
    'focus-visible:ring-white',
    'focus-visible:rounded',
  )

  // Return null for authenticated users while the auth state is being determined
  if (!isVisible) {
    return null
  }

  if (!isNarrow && !isAuthenticated) {
    return (
      <div className={cn('flex flex-row gap-5', headerStyleClasses.textColor)}>
        {menuEntries.map((entry) => (
          <React.Fragment key={entry.name}>
            {isLoginMenuEntry(entry) && (
              <MenuEntryLink
                className={cn(
                  '!font-gotham py-5 text-2xl md:text-base font-medium hover:font-semibold',
                  onFocusStyles,
                )}
                contextBasedContent={entry.contextBasedContent}
                roleBasedContent={entry.roleBasedContent}
                link={entry.link}
                data-cy="login-button"
              >
                {entry.name}
              </MenuEntryLink>
            )}
            {isRegistrationMenuEntry(entry) && (
              <MenuEntryLink
                className={cn(
                  '!font-gotham py-5 text-2xl md:text-base font-medium hover:font-semibold',
                  onFocusStyles,
                )}
                contextBasedContent={entry.contextBasedContent}
                roleBasedContent={entry.roleBasedContent}
                link={entry.link}
              >
                {entry.name}
              </MenuEntryLink>
            )}
          </React.Fragment>
        ))}
      </div>
    )
  }

  return (
    <NavigationMenu aria-label="Utility Menu">
      <NavigationMenuList>
        <NavigationMenuItem className="[&_div]:hidden">
          <NavigationMenuTrigger
            className={cn(headerStyle === 'lightBlue' ? 'brightness-0 invert' : '')}
          >
            <p className="font-gotham text-2xl md:text-sm lg:text-base font-normal md:font-medium m-0 inline-block max-w-[150px] whitespace-nowrap overflow-hidden text-ellipsis">
              {isAuthenticated ? customerDisplayName || 'My account' : 'Account'}
            </p>
          </NavigationMenuTrigger>
          <NavigationMenuContent forceMount className="w-[212px]">
            <ul className="flex flex-col gap-2 !font-gotham text-2xl md:text-base font-normal no-underline border-none bg-transparent">
              {menuEntries.map((entry) => {
                return (
                  <React.Fragment key={entry.name}>
                    {isRegistrationMenuEntry(entry) && (
                      <li>
                        <hr aria-hidden="true" className="border-brand-navy-3 mb-2 w-full" />
                      </li>
                    )}
                    <li>
                      <NavigationMenuLink asChild>
                        <MenuEntryLink
                          className={cn(
                            '!font-gotham text-2xl md:text-base font-normal no-underline border-none bg-transparent',
                            onFocusStyles,
                          )}
                          contextBasedContent={entry.contextBasedContent}
                          roleBasedContent={entry.roleBasedContent}
                          dispensaryContactInformationRequired={
                            entry.dispensaryContactInformationRequired
                          }
                          link={entry.link}
                        >
                          {entry.name}
                        </MenuEntryLink>
                      </NavigationMenuLink>
                    </li>
                  </React.Fragment>
                )
              })}
              {isAuthenticated && (
                <>
                  <li>
                    <hr aria-hidden="true" className="border-brand-navy-3 my-2 w-full" />
                  </li>
                  <li>
                    <NavigationMenuLink asChild>
                      <UI.LogoutLink />
                    </NavigationMenuLink>
                  </li>
                </>
              )}
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  )
}

export const DesktopHeader: React.FC<DesktopHeaderProps> = ({ threshold }) => {
  const showSearch = useShowSearch()
  const { cart } = useCartContext()
  const { toggleCart: toggleCartModal } = useContext(ToggleCartContext)
  const { mainMenu: menuEntriesUnfiltered } = useSiteSettings() || {}
  const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
  const canonicalLocation = useCanonicalLocation()
  const menuEntriesRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { headerStyle, classes: headerStyleClasses } = useHeaderStyle()
  const totalQuantity = totalQuantityExcludingDependents(cart?.items, cart?.total_quantity ?? 0)
  const searchButtonRef = useRef<HTMLButtonElement>(null)
  const wasSearchButtonFocusedRef = useRef(false)
  const [isHovered, setHovered] = useState(false)
  const shouldHideHeader = useShouldHideHeader()
  const activeItem = useActiveItem()

  const toggleSearch = () => {
    if (!showSearch) {
      wasSearchButtonFocusedRef.current = document.activeElement === searchButtonRef.current
    }
    setShowSearch(!showSearch)
  }

  const onSearchClose = useCallback(() => {
    if (wasSearchButtonFocusedRef.current) {
      searchButtonRef.current?.focus()
      wasSearchButtonFocusedRef.current = false
    }
  }, [])

  const handleCartButtonClick = () => {
    toggleCartModal()
    if (cart) {
      publishViewCartEvent(cart)
    }
  }

  useEffect(() => {
    let debounceTimeout: ReturnType<typeof setTimeout>

    const handleMouseMove = (event: MouseEvent) => {
      clearTimeout(debounceTimeout)

      debounceTimeout = setTimeout(() => {
        const entries = menuEntriesRef.current
        const dropdown = dropdownRef.current
        if (!entries || !dropdown) return

        const headerRect = entries.getBoundingClientRect()
        const dropdownRect = dropdown.getBoundingClientRect()
        const { clientX: x, clientY: y } = event

        const inHeader =
          x >= headerRect.left &&
          x <= headerRect.right &&
          y >= headerRect.top &&
          y <= headerRect.bottom
        const inDropdown =
          x >= dropdownRect.left &&
          x <= dropdownRect.right &&
          y >= dropdownRect.top &&
          y <= dropdownRect.bottom

        if (!inHeader && !inDropdown) {
          setActiveItem(null)
        }
      }, 200)
    }

    document.addEventListener('mousemove', handleMouseMove)
    return () => {
      clearTimeout(debounceTimeout)
      document.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!menuEntriesRef.current || !dropdownRef.current) return
      if (
        !menuEntriesRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setActiveItem(null)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    const searchButton = searchButtonRef.current

    const handleFocusOrHover = (event: Event) => {
      const target = event.target as HTMLElement
      const isFocusOnExternalButton = target === searchButton

      if (isFocusOnExternalButton) {
        setActiveItem(null)
      }
    }

    document.addEventListener('focusin', handleFocusOrHover)
    document.addEventListener('mouseover', handleFocusOrHover)

    return () => {
      document.removeEventListener('focusin', handleFocusOrHover)
      document.removeEventListener('mouseover', handleFocusOrHover)
    }
  }, [])

  const handleOnHoverStyling =
    headerStyle === 'transparent' && (isHovered || !!activeItem)
      ? stringifyHeaderStyle(whiteHeaderStyleClasses)
      : headerStyleClasses.backgroundColor

  const menuEntryElements = useMemo(
    () =>
      filterMenuEntriesToDisplay(shouldDisplayContent)(menuEntriesUnfiltered).map((entry) => (
        <DesktopMainMenuEntry
          key={entry._id}
          canonicalLocation={canonicalLocation}
          entry={entry}
          dropdownRef={dropdownRef}
          threshold={threshold}
        />
      )),
    [menuEntriesUnfiltered, shouldDisplayContent, canonicalLocation, threshold],
  )

  return (
    <>
      <UI.Header
        onMouseEnter={() => {
          if (headerStyle === 'transparent') setHovered(true)
        }}
        onMouseLeave={() => {
          if (headerStyle === 'transparent') setHovered(false)
        }}
        className={cn(
          'relative z-50 py-3 px-6',
          shouldHideHeader ? '-translate-y-[200px]' : 'translate-y-0',
          handleOnHoverStyling,
        )}
      >
        <div className="max-w-[1440px] mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/" className="flex flex-row gap-6">
              <UI.DFHLogo />
              <hr
                aria-hidden="true"
                className="h-[50px] my-auto border-r border-brand-rule-grey ml-2"
              />
              <UI.N1PractitionerSupplementBrandLogo />
            </Link>
          </div>

          <NavigationMenu aria-label="Main Navigation Menu" ref={menuEntriesRef}>
            <NavigationMenuList
              className={cn('flex flex-row gap-5 box-content', headerStyleClasses.textColor)}
            >
              {menuEntryElements}
            </NavigationMenuList>
          </NavigationMenu>

          <div className={cn('flex items-center box-border', headerStyleClasses.textColor)}>
            <UI.Button
              ref={searchButtonRef}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={toggleSearch}
              className="md:mr-5 lg:mr-10"
              aria-label="Toggle search"
            >
              <span className="md:hidden lg:inline mr-3">Search</span>
              <UI.Icon alt="Search Icon" src={SearchIcon} aria-hidden="true" />
            </UI.Button>
            <div className="flex flex-row gap-5">
              <AccountDropdown />
              <UI.Button onClick={handleCartButtonClick} aria-label="Toggle mini cart">
                <CartIcon className="font-gotham w-5 h-5" quantity={totalQuantity} />
              </UI.Button>
            </div>
          </div>
        </div>
      </UI.Header>
      <SearchContainer onSearchClose={onSearchClose} />
    </>
  )
}

export const mainMenu = graphql`
  fragment mainMenuFragment on SanitySiteSettings {
    mainMenu {
      ...MenuEntryFragment
      childEntries {
        ...MenuEntryFragment
        childEntries {
          ...MenuEntryFragment
          childEntries {
            ...MenuEntryFragment
          }
        }
      }
    }
  }
`
